import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStore } from '@store'
import {
	StatCard,
	AlertCard,
	TaskList,
	FrameworkProgressList,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	LineChart,
	ProgressRing
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Compliance Manager Dashboard
 *
 * Displays compliance-specific metrics, framework progress,
 * audit readiness, and tasks requiring attention.
 */
const ComplianceManagerDashboard = () => {
	const navigate = useNavigate()

	// Dashboard store
	const {
		complianceOverview,
		frameworkProgress,
		auditReadiness,
		projectsNeedingAttention,
		loading,
		error,
		autoRefreshEnabled,
		fetchComplianceManagerDashboard,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchComplianceManagerDashboard()

		// Start auto-refresh every 60 seconds
		const cleanup = startAutoRefresh('compliance-manager', 60000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchComplianceManagerDashboard, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchComplianceManagerDashboard()
	}, [fetchComplianceManagerDashboard])

	// Quick action handlers
	const quickActions = [
		{
			icon: '📁',
			label: 'Create Project',
			onClick: () => navigate('/projects/new'),
			permission: 'canManageProjects'
		},
		{
			icon: '📝',
			label: 'My Tasks',
			onClick: () => navigate('/tasks/my-tasks'),
			permission: null
		},
		{
			icon: '✅',
			label: 'Submit for Review',
			onClick: () => navigate('/projects?action=submit'),
			permission: null
		},
		{
			icon: '📊',
			label: 'Compliance Reports',
			onClick: () => navigate('/reports/compliance'),
			permission: 'canViewReports'
		}
	]

	// Format projects needing attention for AlertCard
	const attentionAlerts = (projectsNeedingAttention || []).map(project => ({
		id: project.id,
		title: project.name,
		description: project.reason || 'Requires attention',
		type: project.priority === 'high' ? 'danger' : 'warning',
		action: () => navigate(`/projects/${project.id}`),
		actionLabel: 'View'
	}))

	// Handle framework item click
	const handleFrameworkClick = useCallback((item) => {
		navigate(`/projects/${item.id}`)
	}, [navigate])

	// Completion trend data (mock - would come from API)
	const completionTrendData = complianceOverview?.completion_trend || []
	const trendLines = [
		{ dataKey: 'completed', name: 'Completed', color: 'var(--color-success)' }
	]

	return (
		<div className="dashboard-page compliance-manager-dashboard">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<div>
						<h1 className="page-title">Compliance Dashboard</h1>
						<nav className="breadcrumb">
							<span className="breadcrumb__item breadcrumb__item--active">Dashboard</span>
						</nav>
					</div>
					<div className="page-header__actions">
						<span className="last-updated">
							Last updated: {getTimeSinceUpdate()}
							{autoRefreshEnabled && <span className="auto-refresh-badge">Auto</span>}
						</span>
						<button
							className="btn btn--secondary btn--sm"
							onClick={handleRefresh}
							disabled={loading}
						>
							{loading ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert--danger">
					<p>{error}</p>
					<button onClick={handleRefresh}>Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="My Projects"
					value={complianceOverview?.my_projects ?? '-'}
					subtitle="Active projects"
					icon="📁"
					variant="primary"
					loading={loading}
					onClick={() => navigate('/projects')}
				/>
				<StatCard
					title="Draft Projects"
					value={complianceOverview?.draft_projects ?? '-'}
					subtitle="Pending submission"
					icon="📝"
					variant="warning"
					loading={loading}
					onClick={() => navigate('/projects?status=draft')}
				/>
				<StatCard
					title="Tasks In Progress"
					value={complianceOverview?.tasks_in_progress ?? '-'}
					subtitle="Currently working"
					icon="⏳"
					variant="info"
					loading={loading}
					onClick={() => navigate('/tasks/my-tasks?status=in_progress')}
				/>
				<StatCard
					title="Approval Rate"
					value={complianceOverview?.approval_rate ? `${complianceOverview.approval_rate}%` : '-'}
					subtitle="Last 30 days"
					icon="✅"
					variant="success"
					loading={loading}
				/>
			</StatsRow>

			{/* Projects Needing Attention and Audit Readiness Row */}
			<DashboardGrid columns={2}>
				{/* Projects Needing Attention */}
				<WidgetContainer
					title="Projects Needing Attention"
					loading={loading}
				>
					<AlertCard
						items={attentionAlerts}
						type="warning"
						loading={loading}
					/>
				</WidgetContainer>

				{/* Audit Readiness */}
				<WidgetContainer
					title="Audit Readiness"
					onAction={() => navigate('/reports/audit-readiness')}
					actionLabel="View Report"
					loading={loading}
				>
					<div className="audit-readiness-container">
						<div className="audit-readiness__gauge">
							<ProgressRing
								value={auditReadiness?.overall_score || 0}
								max={100}
								size={160}
								strokeWidth={12}
								showPercentage={true}
								label="Overall Score"
								loading={loading}
							/>
						</div>
						<div className="audit-readiness__details">
							<div className="audit-detail">
								<span className="audit-detail__label">Documentation</span>
								<div className="audit-detail__bar">
									<div
										className="audit-detail__fill"
										style={{ width: `${auditReadiness?.documentation_score || 0}%` }}
									/>
								</div>
								<span className="audit-detail__value">{auditReadiness?.documentation_score || 0}%</span>
							</div>
							<div className="audit-detail">
								<span className="audit-detail__label">Evidence Coverage</span>
								<div className="audit-detail__bar">
									<div
										className="audit-detail__fill"
										style={{ width: `${auditReadiness?.evidence_score || 0}%` }}
									/>
								</div>
								<span className="audit-detail__value">{auditReadiness?.evidence_score || 0}%</span>
							</div>
							<div className="audit-detail">
								<span className="audit-detail__label">Control Implementation</span>
								<div className="audit-detail__bar">
									<div
										className="audit-detail__fill"
										style={{ width: `${auditReadiness?.control_score || 0}%` }}
									/>
								</div>
								<span className="audit-detail__value">{auditReadiness?.control_score || 0}%</span>
							</div>
						</div>
					</div>
				</WidgetContainer>
			</DashboardGrid>

			{/* Framework Progress and Completion Trend Row */}
			<DashboardGrid columns={2}>
				{/* Framework Progress */}
				<WidgetContainer
					title="Project Progress"
					onAction={() => navigate('/projects')}
					actionLabel="View All"
					loading={loading}
				>
					<FrameworkProgressList
						items={frameworkProgress || []}
						loading={loading}
						maxHeight={280}
						onItemClick={handleFrameworkClick}
					/>
				</WidgetContainer>

				{/* Completion Trend */}
				<WidgetContainer
					title="Completion Trend (30 Days)"
					loading={loading}
				>
					<LineChart
						data={completionTrendData}
						lines={trendLines}
						xAxisKey="date"
						height={250}
						showGrid={true}
						showLegend={false}
						loading={loading}
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Quick Actions */}
			<QuickActions actions={quickActions} />
		</div>
	)
}

export default ComplianceManagerDashboard
